import { z } from "zod";
import type {
  CustomerAuditSeverity,
  CustomerAuditStage,
  CustomerAuditStatus,
  CustomerAuditType,
} from "@yazamco/shared-types";

/**
 * Customer Audits validation — the single boundary definition shared by the API
 * (authoritative) and the web app.
 *
 * User requests (start / bulk / list / email) are constrained so an arbitrary
 * string can never reach the queue or an artifact path. The result schema is an
 * integrity check applied to the audit engine's own output before it is stored,
 * so a malformed result is never persisted as a "successful" report.
 */

// ── Audit types ─────────────────────────────────────────────────────────────
export const CUSTOMER_AUDIT_TYPES = [
  "negative-findings",
  "monthly-infrastructure",
] as const satisfies readonly CustomerAuditType[];

export const customerAuditTypeSchema = z.enum(CUSTOMER_AUDIT_TYPES);

/** User-facing names. The only place these labels are defined. */
export const CUSTOMER_AUDIT_TYPE_LABELS: Record<CustomerAuditType, string> = {
  "negative-findings": "Negative Findings",
  "monthly-infrastructure": "Monthly Infrastructure Report",
};

/**
 * The report types that may be SENT TO A CUSTOMER.
 *
 * `negative-findings` is deliberately absent, and this is a hard rule rather
 * than a default: it is not a report at all, it is OUR internal list of what
 * has to be fixed at that customer. Emailing it out would hand the customer our
 * own to-do list. Those findings are meant to reach support instead — the
 * support mailbox or an automatically opened ticket — which does not exist yet;
 * until it does, "never emailed to the customer" is enforced here, in the one
 * place both the API and the web app read, instead of being remembered at each
 * of the four call sites that can send mail.
 */
export const CUSTOMER_AUDIT_DELIVERABLE_TYPES = [
  "monthly-infrastructure",
] as const satisfies readonly CustomerAuditType[];

/**
 * The report the platform produces — the only type anything queues today.
 *
 * The audit type is no longer a choice anywhere in the product: the findings
 * are a delivery setting (they go to support), not a second report. The type
 * survives in the schema and in stored rows so historic runs keep rendering as
 * they were, which is why this constant exists instead of the enum being cut
 * down to one member.
 */
export const CUSTOMER_AUDIT_REPORT_TYPE: CustomerAuditType =
  "monthly-infrastructure";

/** Whether a report of this type may be emailed to the customer at all. */
export function isCustomerAuditDeliverable(auditType: CustomerAuditType): boolean {
  return (CUSTOMER_AUDIT_DELIVERABLE_TYPES as readonly string[]).includes(
    auditType,
  );
}

/** The refusal shown when something tries to email an internal-only audit. */
export function customerAuditInternalOnlyMessage(
  auditType: CustomerAuditType,
): string {
  return `${CUSTOMER_AUDIT_TYPE_LABELS[auditType]} is an internal findings list and is never emailed to customers`;
}

// ── Status + stage ──────────────────────────────────────────────────────────
export const CUSTOMER_AUDIT_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
] as const satisfies readonly CustomerAuditStatus[];

export const customerAuditStatusSchema = z.enum(CUSTOMER_AUDIT_STATUSES);

export const CUSTOMER_AUDIT_STAGES = [
  "preparing",
  "collecting_customer_data",
  "collecting_pulseway",
  "collecting_m365",
  "collecting_backups",
  "collecting_firewalls",
  "evaluating_rules",
  "generating_report",
  "generating_pdf",
  "sending_email",
  "completed",
] as const satisfies readonly CustomerAuditStage[];

export const customerAuditStageSchema = z.enum(CUSTOMER_AUDIT_STAGES);

/** Human labels shown next to the progress bar. */
export const CUSTOMER_AUDIT_STAGE_LABELS: Record<CustomerAuditStage, string> = {
  preparing: "Preparing",
  collecting_customer_data: "Collecting customer data",
  collecting_pulseway: "Collecting Pulseway data",
  collecting_m365: "Collecting Microsoft 365 data",
  collecting_backups: "Collecting backup data",
  collecting_firewalls: "Collecting firewall data",
  evaluating_rules: "Evaluating audit rules",
  generating_report: "Generating report",
  generating_pdf: "Generating PDF",
  sending_email: "Sending email",
  completed: "Completed",
};

/** A run is terminal once the worker reported an outcome. */
export function isTerminalCustomerAuditStatus(
  status: CustomerAuditStatus | undefined,
): boolean {
  return status === "succeeded" || status === "failed";
}

export const CUSTOMER_AUDIT_SEVERITIES = [
  "critical",
  "warning",
  "info",
] as const satisfies readonly CustomerAuditSeverity[];

export const customerAuditSeveritySchema = z.enum(CUSTOMER_AUDIT_SEVERITIES);

/** Severity display order (highest first) — shared by the UI and the renderer. */
export const CUSTOMER_AUDIT_SEVERITY_RANK: Record<CustomerAuditSeverity, number> =
  { critical: 0, warning: 1, info: 2 };

// ── Primitives ──────────────────────────────────────────────────────────────

/** `YYYY-MM`. */
export const REPORT_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

export const reportMonthSchema = z
  .string()
  .trim()
  .regex(REPORT_MONTH_PATTERN, "Report month must be formatted YYYY-MM");

/**
 * A customer id as it appears in the customer directory. Constrained to path-
 * and payload-safe characters because it is part of the artifact directory.
 */
export const customerAuditCustomerIdSchema = z
  .string()
  .trim()
  .min(1, "Customer ID is required")
  .max(64)
  .regex(
    /^[A-Za-z0-9._-]+$/,
    "Customer ID may contain only letters, digits, dot, underscore and hyphen",
  );

/** Bulk ceiling — one audit run is heavy (many upstream calls + a PDF render). */
export const MAX_CUSTOMER_AUDIT_BULK_SIZE = 100;

export const customerAuditRecipientsSchema = z
  .array(z.string().trim().email("Invalid recipient address"))
  .max(50);

/** Current month in `YYYY-MM` (UTC), the default report month. */
export function currentReportMonth(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** The calendar month immediately before `reportMonth` (`YYYY-MM`). */
export function previousReportMonth(reportMonth: string): string {
  const [y, m] = reportMonth.split("-").map((p) => Number(p));
  if (!y || !m) return reportMonth;
  const month = m === 1 ? 12 : m - 1;
  const year = m === 1 ? y - 1 : y;
  return `${year}-${String(month).padStart(2, "0")}`;
}

// ── User requests ───────────────────────────────────────────────────────────

/** POST /api/v1/customer-audits/runs — one or many customers. */
export const startCustomerAuditsSchema = z
  .object({
    auditType: customerAuditTypeSchema,
    customerIds: z
      .array(customerAuditCustomerIdSchema)
      .min(1, "Select at least one customer")
      .max(
        MAX_CUSTOMER_AUDIT_BULK_SIZE,
        `A single submission may target at most ${MAX_CUSTOMER_AUDIT_BULK_SIZE} customers`,
      ),
    reportMonth: reportMonthSchema.optional(),
    sendEmail: z.boolean().default(false),
    recipients: customerAuditRecipientsSchema.default([]),
  })
  .strict()
  .transform((data) => ({
    ...data,
    // De-duplicate while preserving the caller's order.
    customerIds: [...new Set(data.customerIds)],
    recipients: [...new Set(data.recipients.map((r) => r.toLowerCase()))],
  }))
  .refine((data) => !data.sendEmail || data.recipients.length > 0, {
    message: "At least one recipient is required when sendEmail is true",
    path: ["recipients"],
  })
  .superRefine((data, ctx) => {
    // An internal-only audit can be RUN with any options except this one.
    if (data.sendEmail && !isCustomerAuditDeliverable(data.auditType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sendEmail"],
        message: customerAuditInternalOnlyMessage(data.auditType),
      });
    }
  });

export type StartCustomerAuditsInput = z.infer<typeof startCustomerAuditsSchema>;

/** GET /api/v1/customer-audits/runs query string. */
export const customerAuditRunsQuerySchema = z.object({
  customerId: customerAuditCustomerIdSchema.optional(),
  auditType: customerAuditTypeSchema.optional(),
  status: customerAuditStatusSchema.optional(),
  reportMonth: reportMonthSchema.optional(),
  batchId: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export type CustomerAuditRunsQuery = z.infer<
  typeof customerAuditRunsQuerySchema
>;

/** POST /api/v1/customer-audits/runs/:runId/email. */
export const emailCustomerAuditReportSchema = z
  .object({
    to: z
      .array(z.string().trim().email("Invalid recipient address"))
      .min(1, "At least one recipient is required")
      .max(50),
    subject: z.string().trim().min(1).max(200).optional(),
  })
  .strict();

export type EmailCustomerAuditReportInput = z.infer<
  typeof emailCustomerAuditReportSchema
>;

// ── Monthly programme ───────────────────────────────────────────────────────

/**
 * An IANA timezone name. Validated by asking the runtime to actually build a
 * formatter for it, so an unknown zone is rejected at the boundary instead of
 * silently making the schedule fire in UTC.
 */
export const ianaTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine((tz) => {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, "Unknown timezone — use an IANA name such as Asia/Jerusalem");

export const CUSTOMER_AUDIT_REPORT_MONTH_MODES = ["previous", "current"] as const;

export const customerAuditReportMonthModeSchema = z.enum(
  CUSTOMER_AUDIT_REPORT_MONTH_MODES,
);

/**
 * PATCH /api/v1/customer-audits/schedule/settings — a partial patch, so the UI
 * can flip one control without restating the rest of the schedule.
 */
export const customerAuditSchedulePatchSchema = z
  .object({
    enabled: z.boolean().optional(),
    // 1–28 only: a programme pinned to the 29th–31st would skip February, which
    // is exactly the month an operator would never notice was missing.
    dayOfMonth: z.coerce.number().int().min(1).max(28).optional(),
    hour: z.coerce.number().int().min(0).max(23).optional(),
    timezone: ianaTimezoneSchema.optional(),
    reportMonthMode: customerAuditReportMonthModeSchema.optional(),
    // Internal recipients of the findings (support), normalized like every
    // other address the module stores. An empty array switches it off.
    findingsRecipients: customerAuditRecipientsSchema.optional(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: "Provide at least one field to update",
  })
  .transform((patch) => ({
    ...patch,
    ...(patch.findingsRecipients
      ? {
          findingsRecipients: [
            ...new Set(patch.findingsRecipients.map((r) => r.toLowerCase())),
          ],
        }
      : {}),
  }));

export type CustomerAuditSchedulePatchInput = z.infer<
  typeof customerAuditSchedulePatchSchema
>;

/** The per-customer decisions: is it scanned, and is the report emailed. */
const subscriptionPatchShape = {
  enabled: z.boolean().optional(),
  auditType: customerAuditTypeSchema.optional(),
  sendEmail: z.boolean().optional(),
  recipients: customerAuditRecipientsSchema.optional(),
} as const;

/**
 * A subscription may only ask for an email if it says who to. The check runs on
 * the MERGED state in the service (a patch that flips `sendEmail` alone is legal
 * when recipients are already stored), so this schema only rejects the case it
 * can decide on its own: turning email on while clearing the recipients.
 */
function rejectEmailWithoutRecipients(patch: {
  sendEmail?: boolean;
  recipients?: string[];
}): boolean {
  return !(patch.sendEmail === true && patch.recipients?.length === 0);
}

/** PUT /api/v1/customer-audits/schedule/subscriptions/:customerId. */
export const customerAuditSubscriptionPatchSchema = z
  .object(subscriptionPatchShape)
  .strict()
  .transform((patch) => ({
    ...patch,
    ...(patch.recipients
      ? { recipients: [...new Set(patch.recipients.map((r) => r.toLowerCase()))] }
      : {}),
  }))
  .refine(rejectEmailWithoutRecipients, {
    message: "At least one recipient is required when sendEmail is true",
    path: ["recipients"],
  });

export type CustomerAuditSubscriptionPatchInput = z.infer<
  typeof customerAuditSubscriptionPatchSchema
>;

/**
 * POST /api/v1/customer-audits/schedule/subscriptions/bulk — the same patch
 * applied to many customers, so "enrol everyone" is one request instead of N.
 */
export const customerAuditSubscriptionBulkSchema = z
  .object({
    customerIds: z
      .array(customerAuditCustomerIdSchema)
      .min(1, "Select at least one customer")
      .max(
        MAX_CUSTOMER_AUDIT_BULK_SIZE,
        `A single request may target at most ${MAX_CUSTOMER_AUDIT_BULK_SIZE} customers`,
      ),
    ...subscriptionPatchShape,
  })
  .strict()
  .transform((body) => ({
    ...body,
    customerIds: [...new Set(body.customerIds)],
    ...(body.recipients
      ? { recipients: [...new Set(body.recipients.map((r) => r.toLowerCase()))] }
      : {}),
  }))
  .refine(rejectEmailWithoutRecipients, {
    message: "At least one recipient is required when sendEmail is true",
    path: ["recipients"],
  })
  .refine(
    (body) =>
      body.enabled !== undefined ||
      body.auditType !== undefined ||
      body.sendEmail !== undefined ||
      body.recipients !== undefined,
    { message: "Provide at least one field to update" },
  );

export type CustomerAuditSubscriptionBulkInput = z.infer<
  typeof customerAuditSubscriptionBulkSchema
>;

// ── Stored result integrity ─────────────────────────────────────────────────

/**
 * A single finding. `findingKey` must be deterministic and stable across months;
 * duplicates are rejected because they would make the diff ambiguous.
 */
export const customerAuditFindingSchema = z
  .object({
    findingKey: z.string().trim().min(1).max(300),
    category: z.string().trim().min(1).max(80),
    control: z.string().trim().min(1).max(120),
    title: z.string().trim().min(1).max(300),
    severity: customerAuditSeveritySchema,
    status: z.string().trim().min(1).max(40).default("open"),
    resourceId: z.string().trim().max(300).nullable().default(null),
    resourceName: z.string().trim().max(300).nullable().default(null),
    value: z.string().trim().max(500).nullable().default(null),
    details: z.record(z.unknown()).default({}),
    sortOrder: z.number().int().nullable().default(null),
  })
  .strict();

export const customerAuditResultSummarySchema = z
  .object({
    critical: z.number().int().min(0).default(0),
    warning: z.number().int().min(0).default(0),
    info: z.number().int().min(0).default(0),
  })
  .catchall(z.number().int());

export const customerAuditWarningSchema = z
  .object({
    source: z.string().trim().min(1).max(120),
    message: z.string().trim().min(1).max(1000),
  })
  .strict();

/**
 * The structured result the audit engine must produce before a run may be marked
 * succeeded. `assets`/`metadata` are typed by the engine in TypeScript and kept
 * as open records here — this schema guards identity, counts and finding keys.
 */
export const customerAuditResultSchema = z.object({
  schemaVersion: z.literal(1),
  customerId: customerAuditCustomerIdSchema,
  customerName: z.string().trim().max(300).default(""),
  auditType: customerAuditTypeSchema,
  reportMonth: reportMonthSchema,
  generatedAt: z.string().datetime({ offset: true }),
  summary: customerAuditResultSummarySchema,
  findings: z
    .array(customerAuditFindingSchema)
    .max(5000)
    .superRefine((findings, ctx) => {
      const seen = new Set<string>();
      for (const [index, finding] of findings.entries()) {
        if (seen.has(finding.findingKey)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, "findingKey"],
            message: `Duplicate findingKey "${finding.findingKey}" — finding keys must be unique within a result`,
          });
        }
        seen.add(finding.findingKey);
      }
    }),
  warnings: z.array(customerAuditWarningSchema).max(200).default([]),
  assets: z.record(z.unknown()).default({}),
  metadata: z.record(z.unknown()).default({}),
});

export type CustomerAuditResultInput = z.infer<
  typeof customerAuditResultSchema
>;

// ── Audit event actions ─────────────────────────────────────────────────────
// Kept as a const map (like the other modules) so the strings that land in
// audit_events — and therefore the webhook dispatch keys — are defined once.
export const CUSTOMER_AUDIT_ACTIONS = {
  batchStarted: "customer_audit_batch_started",
  runQueued: "customer_audit_run_queued",
  runSucceeded: "customer_audit_run_succeeded",
  runFailed: "customer_audit_run_failed",
  runRetried: "customer_audit_run_retried",
  reportEmailed: "customer_audit_report_emailed",
  artifactDownloaded: "customer_audit_artifact_downloaded",
  scheduleUpdated: "customer_audit_schedule_updated",
  subscriptionUpdated: "customer_audit_subscription_updated",
  scheduleFired: "customer_audit_schedule_fired",
} as const;
